import React, { useContext, useEffect, useState, useMemo } from 'react';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { getRequest, postRequest } from '../../../api/ApiController';
import {
  SaveIcon,
  AddIcon,
  CopyIcon,
  VerticalLineIcon,
  BackToFirstIcon,
  BackIcon,
  ForwardIcon,
  ForwardToLastIcon,
  FavoriteIcon,
  PriceIcon,
  SaveAndCloseIcon,
} from '../../svg/svgIcons';
import AuthContext from '../../../components/AuthContext';
import UtilContext from '../../../components/UtilContext';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import { createAsyncNavigateHandlers } from '../../../utils/asyncArrayNavigate';
import { navigationContextToQueryParams } from '../../../utils/maintenanceNavigationContext';
import { uiT as t } from '../../../utils/uiTranslate';

function companyValidityToState(validity) {
  return validity === 1 || validity === '1' ? 1 : 0;
}

export default function MaintenanceCompanyCardDetail({ maintenance_company, type, navigationContext }) {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { openFavoriteDialog, toast, setFastView, setDialogText, setVisible } = useContext(UtilContext);

  const canEditCompany = user?.permissions?.includes('edit maintenance-company');

  const [formCompany, setFormCompany] = useState(maintenance_company);
  const [formType, setFormType] = useState(type);
  const [companyValidity, setCompanyValidity] = useState(() => companyValidityToState(formCompany?.validity));
  const [savingValidity, setSavingValidity] = useState(false);
  const [scrollIcons, setScrollIcons] = useState(() => {
    if (type === 'edit' && maintenance_company) return !canEditCompany;
    return true;
  });

  const company =
    state.maintenance_companies?.find((c) => c.id === formCompany?.id) ?? formCompany;

  useEffect(() => {
    setCompanyValidity(companyValidityToState(company?.validity));
  }, [company?.id, company?.validity]);

  useEffect(() => {
    if (formType === 'edit' && formCompany?.id) {
      setScrollIcons(!canEditCompany);
    } else {
      setScrollIcons(true);
    }
  }, [formCompany, formType, canEditCompany]);

  const { scrollToFirst, scrollUp, scrollDown, scrollToLast } = useMemo(() => {
    const buildNavigateQueryParams = () => {
      const base = navigationContext ? navigationContextToQueryParams(navigationContext) : '';
      const params = new URLSearchParams(base);
      if (!params.has('type')) {
        params.set('type', 'maintenance');
      }
      return params.toString();
    };
    const applyRecord = (data) => {
      if (!data) return;
      setFormCompany(data);
      setCompanyValidity(companyValidityToState(data?.validity));
      setFormType('edit');
      setScrollIcons(!canEditCompany);
    };
    const buildFetchRecord = () => async (direction, currentId) => {
      setDialogText?.(t('Common.LoadingShort'));
      setVisible?.(true);
      try {
        const q = buildNavigateQueryParams();
        const url = `/api/companies/navigate?direction=${direction}${currentId ? `&id=${currentId}` : ''}${q ? `&${q}` : ''}`;
        const resp = await getRequest(url);
        return resp?.data ?? null;
      } finally {
        setVisible?.(false);
      }
    };
    return createAsyncNavigateHandlers(
      buildFetchRecord(),
      applyRecord,
      () => formCompany?.id
    );
  }, [navigationContext, formCompany?.id, canEditCompany, setDialogText, setVisible]);

  const handleSaveCompanyValidity = (closeAfter = false) => {
    if (company?.id == null) return;
    setSavingValidity(true);
    const formData = new FormData();
    formData.append('validity', String(companyValidity));
    postRequest('/api/companies/update/' + company.id, formData, true)
      .then(() => {
        getRequest('/api/companies').then((r) => {
          if (r?.data != null) dispatch({ type: 'load_companies', newCompanies: r.data });
        });
        toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('MaintenanceCompanyCardDetail.Text.Stav_servisnej_firmy_bol_ulozeny') });
        if (closeAfter) dispatch({ type: 'close_tab', id: state.selectedTab });
      })
      .catch(() => alert(t('Common.Toast.SaveFailedAlert')))
      .finally(() => setSavingValidity(false));
  };

  if (!company) {
    return <div className="p-4">Firma nebola nájdená.</div>;
  }

  return (
    <>
      <div>
        <div className="w-full h-10 flex flex-row bg-[#333]">
          <IntersectionObserverWrapper>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="saveandclose">
              <button className="disabled:opacity-25" disabled={savingValidity} onClick={() => handleSaveCompanyValidity(true)}>
                <SaveAndCloseIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="save">
              <button className="disabled:opacity-25" disabled={savingValidity} onClick={() => handleSaveCompanyValidity()}>
                <SaveIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="add">
              <button className="disabled:opacity-25" disabled>
                <AddIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="copy">
              <button className="disabled:opacity-25" disabled>
                <CopyIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="vert">
              <VerticalLineIcon />
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="tofirst">
              <button className="disabled:opacity-25" type="button" onClick={() => scrollToFirst()} disabled={scrollIcons}>
                <BackToFirstIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="toup">
              <button className="disabled:opacity-25" type="button" onClick={() => scrollUp()} disabled={scrollIcons}>
                <BackIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="todown">
              <button className="disabled:opacity-25" type="button" onClick={() => scrollDown()} disabled={scrollIcons}>
                <ForwardIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="tolast">
              <button className="disabled:opacity-25" type="button" onClick={() => scrollToLast()} disabled={scrollIcons}>
                <ForwardToLastIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="vert">
              <VerticalLineIcon />
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="favorite">
              <button onClick={() => openFavoriteDialog('Karta servisnej firmy', 'MaintenanceCompanyCardDetail')}>
                <FavoriteIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="fast">
              <button onClick={() => setFastView('MaintenanceCompanyCardDetail', {}, [])}>
                <PriceIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center " data-targetid="vert">
              <VerticalLineIcon />
            </div>
          </IntersectionObserverWrapper>
        </div>
        <div className="w-full relative flex flex-col min-h-[calc(100vh_-_177px)] overflow-x overflow-y-auto px-4 py-4">
          <div className="xl:grid grid-cols-2 gap-1 mb-2 ">
            <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md">
              <h1 className="text-3xl font-semibold text-left text-zinc-900">KARTA SERVISNEJ FIRMY</h1>
              {formType === 'edit' && <label className="block text-md font-semibold text-gray-800">Por.č.: {company?.id}</label>}
              <div>
                <label htmlFor="name" className="block text-md font-semibold text-gray-800">{t('Common.CompanyNameUpper')}</label>
                <input
                  readOnly={true}
                  name="name"
                  type="text"
                  value={company?.name ?? ''}
                  className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                />
              </div>
              <div>
                <label htmlFor="tel_number" className="block text-md font-semibold text-gray-800">{t('Common.PhoneNumber')}</label>
                <input
                  readOnly={true}
                  name="tel_number"
                  type="text"
                  value={company?.tel_number ?? ''}
                  className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-md font-semibold text-gray-800">
                  E-MAIL
                </label>
                <input
                  readOnly={true}
                  name="email"
                  type="text"
                  value={company?.email ?? ''}
                  className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                />
              </div>
              <div>
                <label htmlFor="contact_person" className="block text-md font-semibold text-gray-800 mt-2">{t('Common.ContactPersonUpper')}</label>
                <input
                  readOnly={true}
                  name="contact_person"
                  type="text"
                  value={company?.contact_person ?? ''}
                  className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                />
              </div>
            </div>
            <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md">
              <div>
                <label htmlFor="address" className="block text-md font-semibold text-gray-800">
                  ADRESA
                </label>
                <input
                  readOnly={true}
                  name="address"
                  type="text"
                  value={company?.address ?? ''}
                  className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                />
              </div>
              <div>
                <label htmlFor="fax" className="block text-md font-semibold text-gray-800">
                  FAX
                </label>
                <input
                  readOnly={true}
                  name="fax"
                  type="text"
                  value={company?.fax ?? ''}
                  className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                />
              </div>
              <div>
                <label htmlFor="web_address" className="block text-md font-semibold text-gray-800">
                  WEB. ADRESA
                </label>
                <input
                  readOnly={true}
                  name="web_address"
                  type="text"
                  value={company?.web_address ?? ''}
                  className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                />
              </div>
              <div>
                <label htmlFor="description" className="block text-md font-semibold text-gray-800">{t('Common.NoteUpper')}</label>
                <textarea
                  readOnly={true}
                  name="gauge_name"
                  type="text"
                  value={company?.description ?? ''}
                  className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                />
              </div>
              <div className="mt-4 pt-4 border-t border-gray-300">
                <h3 className="text-md font-semibold text-gray-800 mb-3">{t('Common.ServiceCompanyStatus')}</h3>
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">{t('Common.Status')}</label>
                  <select
                    value={companyValidity === 1 || companyValidity === '1' ? '1' : '0'}
                    onChange={(e) => setCompanyValidity(e.target.value === '1' ? 1 : 0)}
                    className="block w-full px-3 py-2 text-gray-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring"
                  >
                    <option value="1">{t('Common.Active')}</option>
                    <option value="0">{t('QualityPartnerList.Status.Excluded')}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
